import React, { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";

export function DateValidation({
  invoiceDate,
  minimumDate,
  onValidationChange,
  onDateConverted,
  disabled = false,
}) {
  const [validationResult, setValidationResult] = useState(null);
  const [isValidating, setIsValidating] = useState(false);

  // Uzun tarih formatlarını parse et
  const parseLongDateFormat = (dateStr) => {
    const turkishMonths = {
      ocak: 1,
      şubat: 2,
      mart: 3,
      nisan: 4,
      mayıs: 5,
      haziran: 6,
      temmuz: 7,
      ağustos: 8,
      eylül: 9,
      ekim: 10,
      kasım: 11,
      aralık: 12,
    };

    const englishMonths = {
      january: 1,
      february: 2,
      march: 3,
      april: 4,
      may: 5,
      june: 6,
      july: 7,
      august: 8,
      september: 9,
      october: 10,
      november: 11,
      december: 12,
      jan: 1,
      feb: 2,
      mar: 3,
      apr: 4,
      jun: 6,
      jul: 7,
      aug: 8,
      sep: 9,
      oct: 10,
      nov: 11,
      dec: 12,
    };

    const lowerStr = dateStr.toLowerCase().replace(/,/g, "");

    // Türkçe format: "15 Mart 2024"
    const turkishMatch = lowerStr.match(/(\d{1,2})\s+(\w+)\s+(\d{4})/);
    if (turkishMatch) {
      const [, day, monthName, year] = turkishMatch;
      const month = turkishMonths[monthName];
      if (month) {
        return {
          isValid: true,
          day: parseInt(day),
          month,
          year: parseInt(year),
          format: "gg Ay yıl (TR)",
        };
      }
    }

    // İngilizce format: "March 15 2024" veya "15 March 2024"
    const englishMatch1 = lowerStr.match(/(\w+)\s+(\d{1,2})\s+(\d{4})/);
    if (englishMatch1) {
      const [, monthName, day, year] = englishMatch1;
      const month = englishMonths[monthName];
      if (month) {
        return {
          isValid: true,
          day: parseInt(day),
          month,
          year: parseInt(year),
          format: "Ay gg yıl (EN)",
        };
      }
    }

    const englishMatch2 = lowerStr.match(/(\d{1,2})\s+(\w+)\s+(\d{4})/);
    if (englishMatch2) {
      const [, day, monthName, year] = englishMatch2;
      const month = englishMonths[monthName];
      if (month) {
        return {
          isValid: true,
          day: parseInt(day),
          month,
          year: parseInt(year),
          format: "gg Ay yıl (EN)",
        };
      }
    }

    return { isValid: false };
  };

  // Tarih formatını kontrol et ve parse et (çoklu format desteği)
  const parseDate = (dateStr) => {
    if (!dateStr || typeof dateStr !== "string") {
      return { isValid: false, error: "Tarih boş olamaz" };
    }

    const trimmedDate = dateStr.trim();
    let dayNum, monthNum, yearNum;
    let wasConverted = false;
    let originalFormat = "";
    let convertedFormat = "";

    // 1. gg.ay.yıl formatı (hedef format)
    const dotFormat = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/;
    const dotMatch = trimmedDate.match(dotFormat);

    if (dotMatch) {
      [, dayNum, monthNum, yearNum] = dotMatch.map(Number);
      originalFormat = "gg.ay.yıl";

      // Eğer gün veya ay tek haneli ise (padding gerekiyorsa) dönüştürme olarak işaretle
      const paddedDay = dayNum.toString().padStart(2, "0");
      const paddedMonth = monthNum.toString().padStart(2, "0");
      const properFormat = `${paddedDay}.${paddedMonth}.${yearNum}`;

      if (trimmedDate !== properFormat) {
        wasConverted = true;
        originalFormat = "gg.ay.yıl (padding eklendi)";
      }
    }
    // 2. yyyy-mm-dd formatı (ISO format)
    else if (/^(\d{4})-(\d{1,2})-(\d{1,2})$/.test(trimmedDate)) {
      const isoMatch = trimmedDate.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
      [, yearNum, monthNum, dayNum] = isoMatch.map(Number);
      wasConverted = true;
      originalFormat = "yıl-ay-gün (ISO)";
    }
    // 3. dd/mm/yyyy formatı
    else if (/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.test(trimmedDate)) {
      const slashMatch = trimmedDate.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      const [, first, second, year] = slashMatch.map(Number);

      // Akıllı gün/ay tespiti
      if (first > 12) {
        // İlk rakam 12'den büyükse kesinlikle gün/ay formatı
        dayNum = first;
        monthNum = second;
        originalFormat = "gg/ay/yıl";
      } else if (second > 12) {
        // İkinci rakam 12'den büyükse kesinlikle ay/gün formatı (US)
        monthNum = first;
        dayNum = second;
        originalFormat = "ay/gün/yıl (US)";
      } else {
        // Belirsiz durum - Avrupa formatını varsay (gün/ay)
        dayNum = first;
        monthNum = second;
        originalFormat = "gg/ay/yıl";
      }
      yearNum = year;
      wasConverted = true;
    }
    // 4. dd-mm-yyyy formatı
    else if (/^(\d{1,2})-(\d{1,2})-(\d{4})$/.test(trimmedDate)) {
      const dashMatch = trimmedDate.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
      [, dayNum, monthNum, yearNum] = dashMatch.map(Number);
      wasConverted = true;
      originalFormat = "gg-ay-yıl";
    }
    // 5. Uzun tarih formatları (örn: "15 Mart 2024", "March 15, 2024")
    else {
      const longDateResult = parseLongDateFormat(trimmedDate);
      if (longDateResult.isValid) {
        dayNum = longDateResult.day;
        monthNum = longDateResult.month;
        yearNum = longDateResult.year;
        wasConverted = true;
        originalFormat = longDateResult.format;
      } else {
        return {
          isValid: false,
          error: `Tanınmayan tarih formatı: "${trimmedDate}"
                  
Desteklenen formatlar:
• gg.ay.yıl (15.03.2024) ✅
• yıl-ay-gün (2024-03-15) → 15.03.2024
• gg/ay/yıl (15/03/2024) → 15.03.2024  
• gg-ay-yıl (15-03-2024) → 15.03.2024
• ay/gün/yıl (03/15/2024) → 15.03.2024
• 15 Mart 2024 → 15.03.2024
• March 15 2024 → 15.03.2024`,
        };
      }
    }

    // Hedef format oluştur
    convertedFormat = `${dayNum.toString().padStart(2, "0")}.${monthNum
      .toString()
      .padStart(2, "0")}.${yearNum}`;

    // Tarih değerlerinin geçerliliğini kontrol et
    if (monthNum < 1 || monthNum > 12) {
      return { isValid: false, error: "Ay değeri 1-12 arasında olmalıdır" };
    }

    if (dayNum < 1 || dayNum > 31) {
      return { isValid: false, error: "Gün değeri 1-31 arasında olmalıdır" };
    }

    if (yearNum < 1900 || yearNum > 2100) {
      return {
        isValid: false,
        error: "Yıl değeri 1900-2100 arasında olmalıdır",
      };
    }

    // JavaScript Date objesi oluştur (month 0-indexed)
    const dateObj = new Date(yearNum, monthNum - 1, dayNum);

    // Tarih geçerli mi kontrol et (örn: 31.02.2024 geçersiz)
    if (
      dateObj.getDate() !== dayNum ||
      dateObj.getMonth() !== monthNum - 1 ||
      dateObj.getFullYear() !== yearNum
    ) {
      return { isValid: false, error: "Geçersiz tarih (böyle bir tarih yok)" };
    }

    return {
      isValid: true,
      date: dateObj,
      formatted: convertedFormat,
      originalInput: trimmedDate,
      wasConverted,
      originalFormat,
      convertedFormat,
    };
  };

  const validateDate = async (dateToValidate = null) => {
    try {
      setIsValidating(true);

      const targetDate = dateToValidate || invoiceDate;

      if (!targetDate) {
        const result = {
          success: false,
          error: "Doğrulanacak tarih bulunamadı",
        };
        setValidationResult(result);
        onValidationChange && onValidationChange(result);
        return;
      }

      // Tarih formatını kontrol et
      const parsedDate = parseDate(targetDate);

      if (!parsedDate.isValid) {
        const result = {
          success: false,
          error: parsedDate.error,
        };
        setValidationResult(result);
        onValidationChange && onValidationChange(result);

        toast({
          title: "Tarih Doğrulama Başarısız ❌",
          description: parsedDate.error,
          variant: "destructive",
        });
        return;
      }

      // Minimum tarih kontrolü
      if (minimumDate) {
        let minDateFormatted;
        let minDateObj;

        // Önce string formatında minimum tarihi belirle
        const parsedMinDate = parseDate(minimumDate);
        if (parsedMinDate.isValid) {
          // parseDate başarılı ise onun formatted değerini kullan
          minDateFormatted = parsedMinDate.formatted;
          // Ve tarih objesi olarak da onun tarihini kullan
          minDateObj = parsedMinDate.date;
        } else {
          // parseDate başarısız ise, ISO string olarak deneme yap
          const isoDate = new Date(minimumDate);
          if (!isNaN(isoDate.getTime())) {
            // ISO tarihten UTC extract et
            const day = isoDate.getUTCDate().toString().padStart(2, "0");
            const month = (isoDate.getUTCMonth() + 1)
              .toString()
              .padStart(2, "0");
            const year = isoDate.getUTCFullYear();
            minDateFormatted = `${day}.${month}.${year}`;

            // Aynı tarihi local date objesi olarak oluştur
            minDateObj = new Date(
              year,
              isoDate.getUTCMonth(),
              isoDate.getUTCDate()
            );
          } else {
            console.error("Invalid minimum date:", minimumDate);
            minDateObj = null;
            minDateFormatted = null;
          }
        }

        // Minimum tarih geçerliyse kontrol yap
        if (minDateObj && minDateFormatted) {
          // Sadece tarih karşılaştırması yap (saat bilgisini temizle)
          const minDateOnly = new Date(
            minDateObj.getFullYear(),
            minDateObj.getMonth(),
            minDateObj.getDate()
          );
          const parsedDateOnly = new Date(
            parsedDate.date.getFullYear(),
            parsedDate.date.getMonth(),
            parsedDate.date.getDate()
          );

          if (parsedDateOnly < minDateOnly) {
            const result = {
              success: false,
              error: `Invoice tarihi ${minDateFormatted} tarihinden önce olamaz (aynı gün kabul edilir)`,
            };
            setValidationResult(result);
            onValidationChange && onValidationChange(result);

            toast({
              title: "Tarih Doğrulama Başarısız ❌",
              description: result.error,
              variant: "destructive",
            });
            return;
          }
        }
      }

      // Gelecek tarih kontrolü
      const today = new Date();
      today.setHours(23, 59, 59, 999); // Bugün sonu

      if (parsedDate.date > today) {
        const result = {
          success: false,
          error: "Invoice tarihi gelecek bir tarih olamaz",
        };
        setValidationResult(result);
        onValidationChange && onValidationChange(result);

        toast({
          title: "Tarih Doğrulama Başarısız ❌",
          description: result.error,
          variant: "destructive",
        });
        return;
      }

      // Başarılı doğrulama
      const result = {
        success: true,
        data: {
          isValid: true,
          formatted: parsedDate.formatted,
          originalInput: parsedDate.originalInput,
          wasConverted: parsedDate.wasConverted,
          originalFormat: parsedDate.originalFormat,
          convertedFormat: parsedDate.convertedFormat,
          date: parsedDate.date,
          dayOfWeek: parsedDate.date.toLocaleDateString("tr-TR", {
            weekday: "long",
          }),
        },
      };

      setValidationResult(result);
      onValidationChange && onValidationChange(result);

      // Eğer tarih dönüştürüldüyse ve callback varsa, dönüştürülmüş tarihi geri gönder
      if (parsedDate.wasConverted && onDateConverted) {
        onDateConverted(parsedDate.formatted);
      }

      const toastMessage = parsedDate.wasConverted
        ? `Tarih dönüştürüldü: ${parsedDate.originalInput} (${parsedDate.originalFormat}) → ${parsedDate.formatted} (${result.data.dayOfWeek})`
        : `Tarih geçerli: ${parsedDate.formatted} (${result.data.dayOfWeek})`;

      toast({
        title: parsedDate.wasConverted
          ? "Tarih Dönüştürme ve Doğrulama Başarılı ✅"
          : "Tarih Doğrulama Başarılı ✅",
        description: toastMessage,
        variant: "default",
      });
    } catch (error) {
      console.error("Date validation error:", error);

      const result = {
        success: false,
        error: "Tarih doğrulama sırasında bir hata oluştu",
      };

      setValidationResult(result);
      onValidationChange && onValidationChange(result);

      toast({
        title: "Tarih Doğrulama Hatası",
        description: result.error,
        variant: "destructive",
      });
    } finally {
      setIsValidating(false);
    }
  };

  // Otomatik doğrulama
  useEffect(() => {
    if (invoiceDate && invoiceDate.trim().length > 0) {
      validateDate();
    } else {
      setValidationResult(null);
      onValidationChange && onValidationChange(null);
    }
  }, [invoiceDate, minimumDate]);

  return (
    <div className="mt-2 p-3 bg-muted rounded-lg border">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <h4 className="text-sm font-medium text-foreground">
            Tarih Doğrulama:
          </h4>
          {validationResult && (
            <Badge
              variant={
                validationResult.success && validationResult.data?.isValid
                  ? "default"
                  : "destructive"
              }
            >
              {validationResult.success && validationResult.data?.isValid
                ? `✅ Geçerli (${validationResult.data.formatted})`
                : "❌ Geçersiz"}
            </Badge>
          )}
        </div>

        <div className="flex space-x-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => validateDate()}
            disabled={isValidating || disabled}
          >
            {isValidating
              ? "Kontrol Ediliyor..."
              : validationResult
              ? "🔄 Tekrar Kontrol Et"
              : "Tarih Kontrol Et"}
          </Button>
        </div>
      </div>

      {validationResult && (
        <div className="mt-2 text-xs text-muted-foreground">
          {validationResult.success && validationResult.data?.isValid ? (
            <div className="text-green-600">
              <p>✅ Sonuç: {validationResult.data.formatted}</p>
              <p>📅 Gün: {validationResult.data.dayOfWeek}</p>
              {validationResult.data.wasConverted ? (
                <>
                  <p>
                    🔄 Dönüştürüldü: {validationResult.data.originalInput} (
                    {validationResult.data.originalFormat}) →{" "}
                    {validationResult.data.formatted}
                  </p>
                  <p>✔️ Hedef format: gg.ay.yıl</p>
                </>
              ) : (
                <p>✔️ Format: gg.ay.yıl (zaten doğru)</p>
              )}
              {minimumDate && <p>⏰ Minimum tarih kontrolü: ✅ Geçti</p>}
            </div>
          ) : (
            <div className="text-red-600">
              <p>❌ {validationResult.error}</p>
              <p>💡 Doğru format: gg.ay.yıl (örn: 15.03.2024)</p>
              {minimumDate && (
                <p>
                  ⏰ Minimum tarih:{" "}
                  {(() => {
                    // Aynı logic'i kullan karşılaştırma ile
                    const parsedMinDate = parseDate(minimumDate);
                    if (parsedMinDate.isValid) {
                      return parsedMinDate.formatted;
                    } else {
                      // ISO string deneme - karşılaştırma ile aynı mantık
                      const isoDate = new Date(minimumDate);
                      if (!isNaN(isoDate.getTime())) {
                        const day = isoDate
                          .getUTCDate()
                          .toString()
                          .padStart(2, "0");
                        const month = (isoDate.getUTCMonth() + 1)
                          .toString()
                          .padStart(2, "0");
                        const year = isoDate.getUTCFullYear();
                        return `${day}.${month}.${year}`;
                      }
                      return minimumDate; // Son çare olarak original string'i göster
                    }
                  })()}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
